import { Link } from "react-router-dom";
import { Star, ArrowUpRight } from "lucide-react";
import {
  DARRYL_PHOTO,
  FOUNDER_EDITORIAL_IMAGE,
  FOUNDER_MANIFESTO,
  FOUNDER_VALUES,
  PROFESSIONAL_MEMBERSHIPS,
  FIRM_PHONE,
  MAIN_OFFICE_PHONE,
} from "@/lib/content";
import SEO from "@/components/SEO";
import TestimonialsStrip from "@/components/TestimonialsStrip";
import {
  breadcrumbSchema,
  founderPersonSchema,
} from "@/lib/schemas";
import { SITE } from "@/lib/seo";
import AttorneyReviewNote from "@/components/AttorneyReviewNote";

// ---------------------------------------------------------------------------
// Career milestones — the practice-timeline the /about page currently leaves
// implicit. Rendered as a hairline-divided editorial list on the bio page.
// ---------------------------------------------------------------------------
const CAREER_TIMELINE = [
  {
    year: "1994",
    title: "The University of Oklahoma",
    detail: "Bachelor of Business Administration (B.B.A.).",
  },
  {
    year: "1997",
    title: "Oklahoma City University School of Law",
    detail: "Juris Doctor (J.D.). Admitted to practice by the State Bar of Texas.",
  },
  {
    year: "1997",
    title: "Certified Public Accountant",
    detail: "Licensed CPA in Texas and Oklahoma. Dual credentialing from year one.",
  },
  {
    year: "1998–2008",
    title: "The Big Firms",
    detail: "A decade inside KPMG, Arthur Andersen, and a Dallas partnership representing substantial closely‑held businesses.",
  },
  {
    year: "2001",
    title: "Federal Admissions",
    detail: "U.S. Tax Court · U.S. District Court, Northern & Eastern Districts of Texas · U.S. Bankruptcy Court, N.D. & E.D. Texas.",
  },
  {
    year: "2015",
    title: "Oklahoma Bar Admission",
    detail: "Admitted to practice by the Oklahoma Bar Association — second state license.",
  },
  {
    year: "Present",
    title: "Pratt Law Group, PLLC d/b/a Lone Star Counsel",
    detail: "Founding Managing Partner. Frisco, Texas. Standing counsel to Texas families, business owners, physicians, franchise investors, and multi-generational estates.",
  },
];

const PRACTICE_AREAS = [
  "Business Formation & Corporate Structuring",
  "Mergers, Acquisitions & Succession",
  "Trusts, Estate Planning & Probate",
  "Asset Protection & Wealth Preservation",
  "Bankruptcy & Restructuring · Subchapter V, Chapter 11, Chapter 7",
  "Intellectual Property · Trademarks & Copyrights",
  "Tax Planning & Controversy",
  "Family Office & Private Client Services",
  "Franchise Law",
  "Physician & Healthcare Practice Counsel",
  "Creators, Athletes & Founder-Creator Counsel",
];

const PROFESSIONAL_HIGHLIGHTS = [
  {
    label: "Years in Practice",
    value: "Since 1997",
    detail: "Combined attorney and CPA practice since 1997.",
  },
  {
    label: "Bar Admissions",
    value: "Two States",
    detail: "Texas (1997) and Oklahoma (2015).",
  },
  {
    label: "Federal Courts",
    value: "Three Circuits",
    detail: "U.S. Tax Court, N.D. Texas, E.D. Texas.",
  },
  {
    label: "Discipline",
    value: "Esq. + CPA",
    detail: "Dual credentialing from year one — rare across the Texas bar.",
  },
];

export default function AttorneyDarryl() {
  return (
    <div className="bg-ivory" data-testid="attorney-darryl-page">
      <SEO
        title="Darryl V. Pratt, Esq., CPA — Managing Partner, Lone Star Counsel"
        description="Attorney biography of Darryl V. Pratt — dually licensed Texas attorney and CPA with nearly 30 years of experience in business, succession, trusts, bankruptcy, and intellectual property counsel. Managing Partner, Pratt Law Group, PLLC d/b/a Lone Star Counsel."
        path="/attorneys/darryl-v-pratt"
        schema={[
          founderPersonSchema(),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Attorneys", path: "/attorneys" },
            { name: "Darryl V. Pratt", path: "/attorneys/darryl-v-pratt" },
          ]),
        ]}
      />

      {/* ================================================================
          HERO — editorial magazine spread. Portrait on the right,
          credentialing block on the left. Dark mahogany band.
      ================================================================= */}
      <section
        className="panel-navy grain grain-dark pt-40 pb-24 md:pt-48 md:pb-32 relative overflow-hidden"
        data-testid="attorney-hero"
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left — credentialing */}
            <div className="lg:col-span-7">
              <div className="text-[10px] uppercase tracking-[0.36em] text-brass font-medium">
                Attorney Profile
              </div>
              <div className="mt-3 text-xs uppercase tracking-[0.28em] text-ivory/60">
                Managing Partner · Pratt Law Group, PLLC d/b/a Lone Star Counsel
              </div>

              <h1
                className="mt-10 font-display text-5xl md:text-7xl lg:text-[6.5rem] leading-[0.94] text-ivory tracking-tight"
                data-testid="attorney-name"
              >
                Darryl V.
                <br />
                <span className="italic font-cormorant text-brass">Pratt</span>
              </h1>

              <div className="mt-8 font-cormorant italic text-2xl md:text-3xl text-ivory/85 leading-snug">
                Esq., CPA — dually licensed. Frisco, Texas.
              </div>

              <p className="mt-8 max-w-2xl text-lg md:text-xl text-ivory/85 font-light leading-relaxed">
                A Texas attorney and Certified Public Accountant with nearly 30 years of
                practice. Founder of Pratt Law Group, PLLC d/b/a Lone Star Counsel. Standing counsel to Texas families,
                business owners, physicians, franchise investors, and multi-generational estates.
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-3">
                <Link
                  to="/intake"
                  className="btn-brass inline-flex items-center gap-2"
                  data-testid="attorney-cta-intake"
                >
                  <Star size={11} className="fill-current" />
                  Request a Conference
                </Link>
                <a
                  href={FIRM_PHONE.href || MAIN_OFFICE_PHONE.href}
                  className="text-[10px] uppercase tracking-[0.32em] text-ivory/70 hover:text-brass transition-colors font-medium border-b border-brass/30 hover:border-brass pb-1"
                  data-testid="attorney-cta-phone"
                >
                  or call {FIRM_PHONE.display || MAIN_OFFICE_PHONE.display}
                </a>
              </div>
            </div>

            {/* Right — portrait */}
            <div className="lg:col-span-5">
              <div className="relative">
                <div
                  aria-hidden="true"
                  className="absolute -top-4 -left-4 w-24 h-px bg-brass/60"
                />
                <div
                  aria-hidden="true"
                  className="absolute -top-4 -left-4 w-px h-24 bg-brass/60"
                />
                <img
                  src={DARRYL_PHOTO}
                  alt="Darryl V. Pratt, Managing Partner of Pratt Law Group, PLLC d/b/a Lone Star Counsel"
                  className="w-full h-auto object-cover shadow-2xl"
                  data-testid="attorney-portrait"
                />
                <div
                  aria-hidden="true"
                  className="absolute -bottom-4 -right-4 w-24 h-px bg-brass/60"
                />
                <div
                  aria-hidden="true"
                  className="absolute -bottom-4 -right-4 w-px h-24 bg-brass/60"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================
          AT A GLANCE — four editorial stat pillars.
      ================================================================= */}
      <section className="bg-linen grain py-16 md:py-20" data-testid="attorney-glance">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10">
            {PROFESSIONAL_HIGHLIGHTS.map((h, i) => (
              <div key={h.label} className="hairline-top pt-5" data-testid={`glance-${i}`}>
                <div className="text-[9px] uppercase tracking-[0.32em] text-brass font-medium">
                  {h.label}
                </div>
                <div className="mt-3 font-display text-navy text-3xl md:text-4xl leading-none">
                  {h.value}
                </div>
                <div className="mt-3 text-xs text-slate2 font-light leading-snug">
                  {h.detail}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================================
          IN HIS OWN WORDS — the founder's manifesto.
      ================================================================= */}
      <section className="bg-ivory grain py-24 md:py-32" data-testid="attorney-manifesto">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <aside className="lg:col-span-4">
            <div className="text-[10px] uppercase tracking-[0.36em] text-brass font-medium">
              {FOUNDER_MANIFESTO.kicker}
            </div>
            <h2 className="mt-5 font-display text-navy text-4xl md:text-5xl leading-[1.02]">
              {FOUNDER_MANIFESTO.headline}
              <br />
              <span className="italic font-cormorant text-oxblood">
                {FOUNDER_MANIFESTO.italic}
              </span>
            </h2>
            {FOUNDER_EDITORIAL_IMAGE && (
              <div className="mt-10 relative">
                <img
                  src={FOUNDER_EDITORIAL_IMAGE}
                  alt="Darryl V. Pratt — environmental portrait"
                  className="w-full h-auto object-cover"
                />
              </div>
            )}
          </aside>

          <div className="lg:col-span-8 space-y-6 md:space-y-7">
            {FOUNDER_MANIFESTO.paragraphs.map((p, i) => (
              <p key={i} className="text-slate2 leading-relaxed text-base md:text-lg font-light">
                {p}
              </p>
            ))}
            <div className="pt-6 hairline-top">
              <div className="font-cormorant italic text-brass text-2xl">
                — {FOUNDER_MANIFESTO.signature}
              </div>
              <div className="mt-1 text-[10px] uppercase tracking-[0.28em] text-slate2/70 font-medium">
                {FOUNDER_MANIFESTO.signatureRole}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================
          THREE PRINCIPLES.
      ================================================================= */}
      <section className="bg-linen grain py-24 md:py-32" data-testid="attorney-principles">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="max-w-3xl mb-16">
            <div className="text-[10px] uppercase tracking-[0.36em] text-brass font-medium">
              Three Principles
            </div>
            <h2 className="mt-5 font-display text-navy text-4xl md:text-5xl leading-[1.02]">
              What every engagement is built on.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-10">
            {FOUNDER_VALUES.map((v) => (
              <div key={v.no} className="hairline-top pt-6" data-testid={`principle-${v.no}`}>
                <div className="font-cormorant italic text-brass text-3xl">{v.no}</div>
                <h3 className="mt-4 font-display text-navy text-xl md:text-2xl leading-[1.12]">
                  {v.title}
                </h3>
                <p className="mt-4 text-slate2 leading-relaxed text-[15px]">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================================
          CAREER TIMELINE — editorial hairline list.
      ================================================================= */}
      <section className="bg-ivory grain py-24 md:py-32" data-testid="attorney-timeline">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="max-w-3xl mb-16">
            <div className="text-[10px] uppercase tracking-[0.36em] text-brass font-medium">
              Career Timeline
            </div>
            <h2 className="mt-5 font-display text-navy text-4xl md:text-5xl leading-[1.02]">
              Since 1997, in chapters.
            </h2>
          </div>
          <ol className="space-y-8 md:space-y-10">
            {CAREER_TIMELINE.map((m, i) => (
              <li
                key={i}
                className="hairline-top pt-6 grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8"
                data-testid={`timeline-${i}`}
              >
                <div className="md:col-span-3">
                  <div className="text-[10px] tracking-[0.32em] uppercase text-brass font-medium">
                    No. {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="mt-2 font-cormorant italic text-3xl text-navy leading-none">
                    {m.year}
                  </div>
                </div>
                <div className="md:col-span-9">
                  <h3 className="font-display text-navy text-xl md:text-2xl leading-[1.15]">
                    {m.title}
                  </h3>
                  <p className="mt-3 text-slate2 leading-relaxed text-[15px] max-w-2xl">
                    {m.detail}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ================================================================
          PRACTICE AREAS + PROFESSIONAL MEMBERSHIPS.
      ================================================================= */}
      <section className="bg-linen grain py-24 md:py-32" data-testid="attorney-credentials">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Practice areas */}
          <div className="lg:col-span-6" data-testid="attorney-practice-areas">
            <div className="text-[10px] uppercase tracking-[0.36em] text-brass font-medium">
              Practice Areas
            </div>
            <h2 className="mt-5 font-display text-navy text-3xl md:text-4xl leading-[1.02]">
              Counsel across the continuum.
            </h2>
            <ul className="mt-10 space-y-4">
              {PRACTICE_AREAS.map((a, i) => (
                <li key={i} className="hairline-top pt-3 flex items-baseline gap-4">
                  <span className="text-[10px] tracking-[0.32em] uppercase text-brass font-medium shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-display text-navy text-[15px] md:text-base leading-snug">
                    {a}
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <Link
                to="/practice"
                className="text-[10px] uppercase tracking-[0.28em] text-oxblood hover:text-navy transition-colors font-medium border-b border-brass/60 hover:border-navy pb-1 inline-flex items-center gap-2"
                data-testid="attorney-practice-cta"
              >
                Browse the Full Continuum <ArrowUpRight size={13} />
              </Link>
            </div>
          </div>

          {/* Memberships */}
          <div className="lg:col-span-6" data-testid="attorney-memberships">
            <div className="text-[10px] uppercase tracking-[0.36em] text-brass font-medium">
              Professional Memberships
            </div>
            <h2 className="mt-5 font-display text-navy text-3xl md:text-4xl leading-[1.02]">
              The organizations of record.
            </h2>
            <div className="mt-10 space-y-9">
              {PROFESSIONAL_MEMBERSHIPS.map((section, i) => (
                <div key={i} className="hairline-top pt-5">
                  <div className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium">
                    {section.section}
                  </div>
                  <ul className="mt-3 space-y-1.5">
                    {section.items.map((item, j) => (
                      <li
                        key={j}
                        className="text-[13.5px] text-slate2 leading-snug font-light"
                      >
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================
          TESTIMONIALS — client pullquotes above the closing invitation.
      ================================================================= */}
      <TestimonialsStrip
        variant="ivory"
        kicker="What clients say"
        heading="Craft, judgment, care."
        subhead="Anonymized quotes — attribution held in confidence per the Texas Rules of Professional Conduct."
        limit={6}
      />

      {/* People First — the personal note */}
      <section className="bg-ivory hairline-top py-24 md:py-32" data-testid="attorney-people-first">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-5">
              <div className="image-frame aspect-[4/3]" data-testid="attorney-people-first-image-frame">
                <img
                  src="/img/darryl-murphie.jpg"
                  alt="Darryl V. Pratt, Esq., CPA, Principal and Managing Attorney of Lone Star Counsel, seated at home with his golden retriever Murphie."
                  className="object-cover w-full h-full"
                  loading="lazy"
                  decoding="async"
                  width="1312"
                  height="1199"
                  data-testid="attorney-people-first-image"
                />
              </div>
              <div className="mt-3 text-[10px] uppercase tracking-[0.28em] text-brass/70 font-medium">
                At home · with Murphie
              </div>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <div className="kicker">People First</div>
              <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.05] text-navy" data-testid="attorney-people-first-headline">
                Why I do this{" "}
                <span className="italic text-oxblood">work.</span>
              </h2>
              <p className="mt-8 font-cormorant italic text-2xl md:text-3xl text-navy/80 leading-snug" data-testid="attorney-people-first-caption">
                Counsel is personal. Every business, family, and legacy deserves thoughtful guidance from someone who listens.
              </p>
              <p className="mt-8 text-lg text-navy/75 leading-relaxed">
                A small roster, on purpose. Long relationships, by design. Faith, family, clients, community — in that order, and steadily, for the ones we serve.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================
          CLOSING CTA.
      ================================================================= */}
      <section
        className="panel-navy grain grain-dark py-20 md:py-28 relative overflow-hidden"
        data-testid="attorney-cta"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <Star size={11} className="text-brass fill-brass mx-auto" />
          <div className="mt-6 text-[10px] uppercase tracking-[0.36em] text-brass font-medium">
            A Private Invitation
          </div>
          <h2
            className="mt-8 font-display text-ivory text-4xl md:text-5xl lg:text-6xl leading-[1.02] max-w-4xl mx-auto"
            data-testid="attorney-closing-headline"
          >
            If your matter warrants standing counsel,{" "}
            <span className="italic font-cormorant text-brass">
              the desk is available.
            </span>
          </h2>
          <p className="mt-8 max-w-2xl mx-auto font-cormorant italic text-lg md:text-xl text-ivory/80 leading-snug">
            A confidential conference. Same-day response. Frisco headquarters, or by
            secure video across the state of Texas.
          </p>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/intake"
              className="btn-brass inline-flex items-center gap-2"
              data-testid="attorney-closing-cta"
            >
              <Star size={11} className="fill-current" />
              Request a Confidential Conference
            </Link>
            <a
              href={FIRM_PHONE.href || MAIN_OFFICE_PHONE.href}
              className="text-[11px] uppercase tracking-[0.32em] text-ivory/85 hover:text-brass transition-colors font-medium border-b border-brass/50 hover:border-brass pb-1"
              data-testid="attorney-closing-phone"
            >
              or call {FIRM_PHONE.display || MAIN_OFFICE_PHONE.display}
            </a>
          </div>
          <div className="mt-10 text-[10px] uppercase tracking-[0.28em] text-ivory/50">
            {SITE.name} · Frisco, Texas
          </div>
          <div className="max-w-3xl mx-auto">
            <AttorneyReviewNote reviewed="February 2026" variant="dark" />
          </div>
        </div>
      </section>
    </div>
  );
}
